import chalk from 'chalk';
import { existsSync, readFileSync } from 'fs';
import { join } from 'path';
import type { Command } from 'commander';
import type { OutputFormat } from '../types/index.js';

// ---------------------------------------------------------------------------
// Compat metadata (Scope D)
// ---------------------------------------------------------------------------

interface CompatMetadata {
  cliVersion: string;
  skillVersion: string;
  skillMinCliVersion: string;
}

let _compatCache: CompatMetadata | null = null;
let _globalCliVersion = '0.0.0';

/**
 * Set the global CLI version for compat metadata injection.
 * Called once from cli.ts after the version is resolved.
 */
export function setCliVersion(version: string): void {
  _globalCliVersion = version;
  _compatCache = null; // reset cache when version changes
}

/**
 * Build the _compat metadata block included in all --json output.
 * Reads SKILL.md frontmatter for metadata.skill_version and metadata.skill_min_cli_version.
 * Results are cached after the first call (process-lifetime cache).
 *
 * Per D4 (locked): warn-but-not-block when cliVersion < skillMinCliVersion.
 * The Skill compares these values and emits a prominent warn but continues.
 */
export function getCompatMetadata(cliVersion: string = _globalCliVersion): CompatMetadata {
  if (_compatCache !== null) return _compatCache;

  let skillVersion = 'unknown';
  let skillMinCliVersion = 'unknown';

  try {
    // Look for installed skill first (runtime path), then source path
    const candidatePaths = [
      join(process.cwd(), '.claude', 'skills', 'mutagent-cli', 'SKILL.md'),
      join(process.cwd(), 'mutagent-cli', '.claude', 'skills', 'mutagent-cli', 'SKILL.md'),
    ];
    for (const p of candidatePaths) {
      if (existsSync(p)) {
        const content = readFileSync(p, 'utf-8');
        // New spec shape: keys are indented under `metadata:` block, values are quoted strings.
        // Regex strips optional surrounding double-quotes from the captured value.
        const verMatch = /^\s+skill_version:\s+"?([^"\n]+)"?\s*$/m.exec(content);
        const minMatch = /^\s+skill_min_cli_version:\s+"?([^"\n]+)"?\s*$/m.exec(content);
        // Hard-error guard: if a `metadata:` block exists but neither key is found, throw so
        // the bug surfaces in CI rather than silently returning "unknown".
        if (/^metadata:/m.test(content) && !verMatch && !minMatch) {
          throw new Error(
            'Bundled SKILL.md is missing metadata.skill_version / skill_min_cli_version' +
            ' — re-run `bun run sync-skill` to regenerate.'
          );
        }
        if (verMatch?.[1]) skillVersion = verMatch[1].trim();
        if (minMatch?.[1]) skillMinCliVersion = minMatch[1].trim();
        break;
      }
    }
  } catch (err) {
    // Pre-existing tolerance: SKILL.md may not exist in either candidate path (e.g. invoked
    // outside a project dir). That's OK — fall back to "unknown".
    // BUT: a `metadata:` block exists with neither expected key means sync-skill is broken.
    // Per plan D5 — fail loud so CI catches it, even if it crashes one CLI invocation.
    if (err instanceof Error && err.message.startsWith('Bundled SKILL.md is missing')) {
      throw err;
    }
  }

  _compatCache = { cliVersion, skillVersion, skillMinCliVersion };
  return _compatCache;
}

/**
 * Safely extract the --json flag from Commander options chain.
 * Handles the `any` type from Commander's opts() method.
 */
export function getJsonFlag(command: Command | undefined): boolean {
  if (!command) return false;
  // Navigate up the parent chain to find the root command options
   
  const parentOpts = command.parent ? command.parent.opts() : null;
   
  const ownOpts = command.opts();
   
  return Boolean(parentOpts?.json ?? ownOpts.json);
}

export class OutputFormatter {
  constructor(private format: OutputFormat) {}

  output(data: unknown): void {
    if (this.format === 'json') {
      // Scope D: inject _compat into all directive-carrying JSON output
      let enriched = data;
      if (
        _globalCliVersion !== '0.0.0' &&
        data !== null &&
        typeof data === 'object' &&
        !Array.isArray(data) &&
        '_directive' in (data as Record<string, unknown>)
      ) {
        enriched = {
          ...(data as Record<string, unknown>),
          _compat: getCompatMetadata(_globalCliVersion),
        };
      }
      console.log(JSON.stringify(enriched, null, 2));
    } else {
      this.formatTable(data);
    }
  }

  private formatTable(data: unknown): void {
    if (Array.isArray(data)) {
      this.formatArray(data);
    } else if (data && typeof data === 'object') {
      this.formatObject(data as Record<string, unknown>);
    } else {
      console.log(data);
    }
  }

  private formatArray(data: unknown[]): void {
    if (data.length === 0) {
      console.log(chalk.gray('No results'));
      return;
    }

    // Get columns from first item
    const firstItem = data[0];
    if (!firstItem || typeof firstItem !== 'object') {
      data.forEach(item => { console.log(`  ${String(item)}`); });
      return;
    }

    const keys = Object.keys(firstItem);
    
    // Helper to convert any value to display string
    const toDisplayString = (val: unknown): string => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') return JSON.stringify(val);
      if (typeof val === 'string') return val;
      if (typeof val === 'number' || typeof val === 'boolean' || typeof val === 'bigint') {
        return val.toString();
      }
      return JSON.stringify(val);
    };

    // Calculate column widths
    const widths: Record<string, number> = {};
    keys.forEach(key => {
      widths[key] = Math.max(
        key.length,
        ...data.map(item => toDisplayString((item as Record<string, unknown>)[key]).length)
      );
    });

    // Print header
    const header = keys.map(key => chalk.bold(key.toUpperCase().padEnd(widths[key] ?? 10))).join('  ');
    console.log(header);
    console.log(keys.map(key => '-'.repeat(widths[key] ?? 10)).join('  '));

    // Print rows
    data.forEach(item => {
      const row = keys.map(key => {
        const value = toDisplayString((item as Record<string, unknown>)[key]);
        const width = widths[key] ?? 10;
        return value.padEnd(width).slice(0, width);
      }).join('  ');
      console.log(row);
    });

    console.log(chalk.gray(`\n${String(data.length)} result(s)`));
  }

  private formatObject(data: Record<string, unknown>): void {
    const maxKeyLength = Math.max(...Object.keys(data).map(k => k.length));

    Object.entries(data).forEach(([key, value]) => {
      const formattedKey = chalk.bold(key.padEnd(maxKeyLength));
      let formattedValue: string;

      if (value === null || value === undefined) {
        formattedValue = '';
      } else if (typeof value === 'object') {
        formattedValue = JSON.stringify(value, null, 2);
      } else if (typeof value === 'string') {
        formattedValue = value;
      } else if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        formattedValue = value.toString();
      } else {
        formattedValue = JSON.stringify(value);
      }

      console.log(`${formattedKey}  ${formattedValue}`);
    });
  }

  success(message: string): void {
    if (this.format === 'json') {
      console.log(JSON.stringify({ success: true, message }, null, 2));
    } else {
      console.log(chalk.green(`✓ ${message}`));
    }
  }

  error(message: string, code?: string, suggestedAction?: string): void {
    if (this.format === 'json') {
      const result: Record<string, unknown> = { success: false, error: message };
      if (code) result.code = code;
      if (suggestedAction) result.suggestedAction = suggestedAction;
      console.log(JSON.stringify(result, null, 2));
    } else {
      console.log(chalk.red(`✗ ${message}`));
    }
  }

  info(message: string): void {
    if (this.format !== 'json') {
      console.log(chalk.blue(`ℹ ${message}`));
    }
  }

  warn(message: string): void {
    if (this.format === 'json') {
      console.log(JSON.stringify({ warning: message }, null, 2));
    } else {
      console.log(chalk.yellow(`⚠ ${message}`));
    }
  }
}

interface Spinner {
  start: () => Promise<unknown> | undefined;
  stop?: () => void;
  succeed?: () => void;
  fail?: () => void;
}

export function createSpinner(text: string, isJson: boolean): Spinner {
  if (isJson) {
    // No-op spinner for JSON mode - returns undefined to match interface
    const noop = (): undefined => undefined;
    return {
      start: noop,
      stop: noop,
      succeed: noop,
      fail: noop,
    };
  }

  // Dynamic import for ora (ESM)
  const start = async (): Promise<unknown> => {
    const { default: ora } = await import('ora');
    return ora(text).start();
  };

  return { start };
}
